import css from './index.module.scss';
import { IconNode, Modal, Typography } from '@components/base';
import Images from '@assets/images';
import {
  calculateSavedQuoteCMValues,
  getCommaSeperatedCurrencyAmount,
  sanitizeValue
} from '@helpers/utils';
import { ENQUIRY_TYPE } from '@helpers/constants';
import CMValue from '@components/common/quote-wrapper/components/cm-value';

const ViewQuoteModal = (props: any) => {
  const { open, onClose, data, isConsolidation, primaryUnit, enquiryType, cm2Context } = props;

  const isDomestic = enquiryType === ENQUIRY_TYPE.DOMESTIC;
  const getFormattedCM = (cm: 'cm1' | 'cm2', quote: any, consolidationQuote?: any) => {
    if (!cm2Context?.currency1) return '-';
    const cmValues = calculateSavedQuoteCMValues(
      quote,
      { isDomestic, ...cm2Context },
      consolidationQuote
    );
    return cm === 'cm1' ? (
      <CMValue
        amount={cmValues.cm1}
        percent={cmValues.cm1Percent}
        currency={cm2Context.currency1.label}
      />
    ) : (
      <CMValue
        amount={cmValues.cm2}
        percent={cmValues.cm2Percent}
        currency={cm2Context.currency1.label}
      />
    );
  };
  const renderDomesticContent = () => {
    return (
      <section className={css.modalContent}>
        <div className={css.rowWrapper}>
          <div className={css.keyValue}>
            <div className={css.key}>Product Name</div>
            <div className={css.value}>{sanitizeValue(data?.product_name)}</div>
          </div>
          <div className={css.keyValue}>
            <div className={css.key}>Deal Quantity</div>
            <div className={css.value}>
              {Number(data?.quantity)} {data?.quantity_unit}
            </div>
          </div>
        </div>

        <div className={css.rowWrapper}>
          <div className={css.keyValue}>
            <div className={css.key}>Product Packaging</div>
            <div className={css.value}>{sanitizeValue(data?.packaging)}</div>
          </div>
          <div className={css.keyValue}>
            <div className={css.key}>MOQ</div>
            <div className={css.value}>
              {Number(data?.moq)} {data?.moq_unit}
            </div>
          </div>
        </div>

        <div className={css.rowWrapper}>
          <div className={css.keyValue}>
            <div className={css.key}>
              {
                <span>
                  Base Price
                  {data?.base_price && (
                    <Typography variant="label">
                      {' '}
                      ( Per {data?.base_price?.unit_of_weight} )
                    </Typography>
                  )}
                </span>
              }
            </div>
            <div className={css.value}>
              {data?.base_price != null
                ? getCommaSeperatedCurrencyAmount(
                    data?.base_price?.currency,
                    Number(data?.base_price?.value)
                  )
                : '-'}
            </div>
          </div>
          <div className={css.keyValue}>
            <div className={css.key}>
              {
                <span>
                  Final Price
                  {data?.final_price && (
                    <Typography variant="label">
                      {' '}
                      ( Per {data?.final_price?.unit_of_weight} )
                    </Typography>
                  )}
                </span>
              }
            </div>
            <div className={css.value}>
              {data?.final_price != null
                ? getCommaSeperatedCurrencyAmount(
                    data?.final_price?.currency,
                    Number(data?.final_price?.value)
                  )
                : '-'}
            </div>
          </div>
        </div>

        <div className={css.rowWrapper}>
          <div className={css.keyValue}>
            <div className={css.key}>Total Local Transportation</div>
            <div className={css.value}>
              {data?.total_local_freight != null
                ? getCommaSeperatedCurrencyAmount(
                    data?.total_local_freight?.currency,
                    Number(data?.total_local_freight?.value)
                  )
                : '-'}
            </div>
          </div>
          <div className={css.keyValue}>
            <div className={css.key}>Warehousing Cost</div>
            <div className={css.value}>
              {data?.warehousing_cost != null
                ? getCommaSeperatedCurrencyAmount(
                    data?.warehousing_cost?.currency,
                    Number(data?.warehousing_cost?.value)
                  )
                : '-'}
            </div>
          </div>
        </div>

        <div className={css.rowWrapper}>
          <div className={css.keyValue}>
            <div className={css.key}>
              {
                <span>
                  Additional Costs
                  {primaryUnit && <Typography variant="label"> ( Per {primaryUnit} )</Typography>}
                </span>
              }
            </div>
            <div className={css.value}>
              {data?.additional_cost != null
                ? getCommaSeperatedCurrencyAmount(
                    data?.additional_cost?.currency,
                    Number(data?.additional_cost?.value)
                  )
                : '-'}
            </div>
          </div>
          <div className={css.keyValue}>
            <div className={css.key}>Finance Cost</div>
            <div className={css.value}>
              {data?.finance_cost ? `${sanitizeValue(data?.finance_cost)} %` : '-'}
            </div>
          </div>
        </div>

        <div className={css.rowWrapper}>
          <div className={css.keyValue}>
            <div className={css.key}>Expected Profit Margin</div>
            <div className={css.value}>
              {data?.profit_margin ? `${sanitizeValue(data?.profit_margin)} %` : '0 %'}
            </div>
          </div>
          <div className={css.keyValue}>
            <div className={css.key}>Discount</div>
            <div className={css.value}>
              {data?.discount ? `${sanitizeValue(data?.discount)} %` : '-'}
            </div>
          </div>
        </div>

        <div className={css.rowWrapper}>
          <div className={css.keyValue}>
            <div className={css.key}>Expected CM1</div>
            <div className={css.value}>{getFormattedCM('cm1', data)}</div>
          </div>
          <div className={css.keyValue}>
            <div className={css.key}>Expected CM2</div>
            <div className={css.value}>{getFormattedCM('cm2', data)}</div>
          </div>
        </div>

        <div className={css.rowWrapper}>
          <div className={css.keyValue}>
            <div className={css.key}>Origin</div>
            <div className={css.value}>
              {sanitizeValue(data?.origin_display_value ?? data?.origin)}
            </div>
          </div>
          <div className={css.keyValue}>
            <div className={css.key}>Zip Code</div>
            <div className={css.value}>{sanitizeValue(data?.zip_code)}</div>
          </div>
        </div>
        <div className={css.rowWrapper}>
          <div className={css.keyValue}>
            <div className={css.key}>Material Location Country</div>
            <div className={css.value}>
              {sanitizeValue(
                data?.material_location_country_display_value ?? data?.material_location_country
              )}
            </div>
          </div>
        </div>

        {data?.deal_incoterm_location && (
          <div className={css.rowWrapper}>
            <div className={css.keyValue}>
              <div className={css.key}>Place of Discharge</div>
              <div className={css.value}>{sanitizeValue(data?.deal_incoterm_location)}</div>
            </div>
          </div>
        )}
      </section>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className={css.modalWrapper}>
        <section className={css.modalHeader}>
          <Typography variant="h4">Quote Details</Typography>
          <IconNode
            src={Images.crossBlack}
            alt="close icon"
            component="button"
            className={css.closeButton}
            onClick={() => {
              onClose();
            }}
          />
        </section>

        {isDomestic ? (
          renderDomesticContent()
        ) : isConsolidation ? (
          <section className={css.modalContent}>
            {data?.products.map((product: any, index: number) => {
              return (
                <div key={index} className={css.productWrapper}>
                  <div className={css.rowWrapper}>
                    <div className={css.keyValue}>
                      <div className={css.key}>Product Name</div>
                      <div className={css.value}>{sanitizeValue(product?.product_data?.name)}</div>
                    </div>
                    <div className={css.keyValue}>
                      <div className={css.key}>Deal Quantity</div>
                      <div className={css.value}>
                        {Number(product?.quantity)} {product?.quantity_unit}
                      </div>
                    </div>
                  </div>
                  <div className={css.rowWrapper}>
                    <div className={css.keyValue}>
                      <div className={css.key}>Product Packaging</div>
                      <div className={css.value}>{sanitizeValue(product?.packaging)}</div>
                    </div>
                    <div className={css.keyValue}>
                      <div className={css.key}>MOQ</div>
                      <div className={css.value}>
                        {Number(product?.moq)} {product?.moq_unit}
                      </div>
                    </div>
                  </div>
                  <div className={css.rowWrapper}>
                    <div className={css.keyValue}>
                      <div className={css.key}>
                        {
                          <span>
                            Base Price
                            {product?.base_price && (
                              <Typography variant="label">
                                {' '}
                                ( Per {product?.base_price?.unit_of_weight} )
                              </Typography>
                            )}
                          </span>
                        }
                      </div>
                      <div className={css.value}>
                        {product?.base_price != null
                          ? getCommaSeperatedCurrencyAmount(
                              product?.base_price?.currency,
                              Number(product?.base_price?.value)
                            )
                          : '-'}
                      </div>
                    </div>
                    <div className={css.keyValue}>
                      <div className={css.key}>
                        {
                          <span>
                            Ex-works Price
                            {product?.ex_works_price && (
                              <Typography variant="label">
                                {' '}
                                ( Per {product?.ex_works_price?.unit_of_weight} )
                              </Typography>
                            )}
                          </span>
                        }
                      </div>
                      <div className={css.value}>
                        {product?.ex_works_price != null
                          ? getCommaSeperatedCurrencyAmount(
                              product?.ex_works_price?.currency,
                              Number(product?.ex_works_price?.value)
                            )
                          : '-'}
                      </div>
                    </div>
                  </div>
                  <div className={css.rowWrapper}>
                    <div className={css.keyValue}>
                      <div className={css.key}>
                        {
                          <span>
                            FOB Price
                            {product?.fob_price && (
                              <Typography variant="label">
                                {' '}
                                ( Per {product?.fob_price?.unit_of_weight} )
                              </Typography>
                            )}
                          </span>
                        }
                      </div>
                      <div className={css.value}>
                        {product?.fob_price != null
                          ? getCommaSeperatedCurrencyAmount(
                              product?.fob_price?.currency,
                              Number(product?.fob_price?.value)
                            )
                          : '-'}
                      </div>
                    </div>
                    <div className={css.keyValue}>
                      <div className={css.key}>
                        {
                          <span>
                            CFR Price
                            {product?.cfr_price && (
                              <Typography variant="label">
                                {' '}
                                ( Per {product?.cfr_price?.unit_of_weight} )
                              </Typography>
                            )}
                          </span>
                        }
                      </div>
                      <div className={css.value}>
                        {product?.cfr_price != null
                          ? getCommaSeperatedCurrencyAmount(
                              product?.cfr_price?.currency,
                              Number(product?.cfr_price?.value)
                            )
                          : '-'}
                      </div>
                    </div>
                  </div>
                  <div className={css.rowWrapper}>
                    <div className={css.keyValue}>
                      <div className={css.key}>
                        {
                          <span>
                            CIF Price
                            {product?.cif_price && (
                              <Typography variant="label">
                                {' '}
                                ( Per {product?.cif_price?.unit_of_weight} )
                              </Typography>
                            )}
                          </span>
                        }
                      </div>
                      <div className={css.value}>
                        {product?.cif_price != null
                          ? getCommaSeperatedCurrencyAmount(
                              product?.cif_price?.currency,
                              Number(product?.cif_price?.value)
                            )
                          : '-'}
                      </div>
                    </div>
                    <div className={css.keyValue}>
                      <div className={css.key}>
                        {
                          <span>
                            Final Price
                            {product?.final_price && (
                              <Typography variant="label">
                                {' '}
                                ( Per {product?.final_price?.unit_of_weight} )
                              </Typography>
                            )}
                          </span>
                        }
                      </div>
                      <div className={css.value}>
                        {product?.final_price != null
                          ? getCommaSeperatedCurrencyAmount(
                              product?.final_price?.currency,
                              Number(product?.final_price?.value)
                            )
                          : '-'}
                      </div>
                    </div>
                  </div>
                  <div className={css.rowWrapper}>
                    <div className={css.keyValue}>
                      <div className={css.key}>Expected Profit Margin</div>
                      <div className={css.value}>
                        {product?.profit_margin
                          ? ` ${sanitizeValue(product?.profit_margin)} % `
                          : '0 %'}
                      </div>
                    </div>
                    <div className={css.keyValue}>
                      <div className={css.key}>Country of Origin</div>
                      <div className={css.value}>
                        {sanitizeValue(product?.origin_display_value ?? product?.origin)}
                      </div>
                    </div>
                  </div>
                  <div className={css.rowWrapper}>
                    <div className={css.keyValue}>
                      <div className={css.key}>Material Location Country</div>
                      <div className={css.value}>
                        {sanitizeValue(
                          product?.material_location_country_display_value ??
                            product?.material_location_country
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={css.rowWrapper}>
                    <div className={css.keyValue}>
                      <div className={css.key}>Expected CM1</div>
                      <div className={css.value}>{getFormattedCM('cm1', product, data)}</div>
                    </div>
                    <div className={css.keyValue}>
                      <div className={css.key}>Expected CM2</div>
                      <div className={css.value}>{getFormattedCM('cm2', product, data)}</div>
                    </div>
                  </div>
                  <div className={css.rowWrapper}>
                    <div className={css.keyValue}>
                      <div className={css.key}>
                        {
                          <span>
                            Additional Costs
                            {primaryUnit && (
                              <Typography variant="label"> ( Per {primaryUnit} )</Typography>
                            )}
                          </span>
                        }
                      </div>
                      <div className={css.value}>
                        {product?.additional_cost != null
                          ? getCommaSeperatedCurrencyAmount(
                              product?.additional_cost?.currency,
                              Number(product?.additional_cost?.value)
                            )
                          : '-'}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>Total Freight </div>
                <div className={css.value}>
                  {data?.total_freight != null
                    ? getCommaSeperatedCurrencyAmount(
                        data?.total_freight?.currency,
                        Number(data?.total_freight?.value)
                      )
                    : '-'}
                </div>
              </div>
              <div className={css.keyValue}>
                <div className={css.key}>POL</div>
                <div className={css.value}>{sanitizeValue(data?.pol?.name)}</div>
              </div>
            </div>
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>POD</div>
                <div className={css.value}>{sanitizeValue(data?.pod?.name)}</div>
              </div>
              <div className={css.keyValue}>
                <div className={css.key}>Credit and Cargo insurance cost</div>
                <div className={css.value}>
                  {data?.insurance_cost ? ` ${sanitizeValue(data?.insurance_cost)} % ` : '-'}
                </div>
              </div>
            </div>
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>Incoterms </div>
                <div className={css.value}>{sanitizeValue(data?.deal_incoterm)}</div>
              </div>
              <div className={css.keyValue}>
                <div className={css.key}>Discount</div>
                <div className={css.value}>
                  {data?.discount ? ` ${sanitizeValue(data?.discount)} % ` : '-'}
                </div>
              </div>
            </div>
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>Total Local Transportation & Freight</div>
                <div className={css.value}>
                  {data?.total_local_freight != null
                    ? getCommaSeperatedCurrencyAmount(
                        data?.total_local_freight?.currency,
                        Number(data?.total_local_freight?.value)
                      )
                    : '-'}
                </div>
              </div>
              <div className={css.keyValue}>
                <div className={css.key}>Total port and clearance expenses</div>
                <div className={css.value}>
                  {data?.total_port_and_clearance_expenses != null
                    ? getCommaSeperatedCurrencyAmount(
                        data?.total_port_and_clearance_expenses?.currency,
                        Number(data?.total_port_and_clearance_expenses?.value)
                      )
                    : '-'}
                </div>
              </div>
            </div>
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>Finance Cost</div>
                <div className={css.value}>
                  {data?.finance_cost ? ` ${sanitizeValue(data?.finance_cost)} % ` : '-'}
                </div>
              </div>
              <div className={css.keyValue}>
                <div className={css.key}>Total weight</div>
                <div className={css.value}>
                  {Number(data?.total_weight)} {data?.total_weight_unit}
                </div>
              </div>
            </div>
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>
                  {data?.deal_incoterm === 'EXW' ? 'Ex Works Location' : 'Place of Discharge'}
                </div>
                <div className={css.value}>{sanitizeValue(data?.deal_incoterm_location)}</div>
              </div>
            </div>
          </section>
        ) : (
          <section className={css.modalContent}>
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>Product Name</div>
                <div className={css.value}>{sanitizeValue(data?.product_name)}</div>
              </div>
              <div className={css.keyValue}>
                <div className={css.key}>Deal Quantity</div>
                <div className={css.value}>
                  {Number(data?.quantity)} {data?.quantity_unit}
                </div>
              </div>
            </div>
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>Product Packaging</div>
                <div className={css.value}>{sanitizeValue(data?.packaging)}</div>
              </div>
              <div className={css.keyValue}>
                <div className={css.key}>MOQ</div>
                <div className={css.value}>
                  {Number(data?.moq)} {data?.moq_unit}
                </div>
              </div>
            </div>
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>
                  {
                    <span>
                      Base Price
                      {data?.base_price && (
                        <Typography variant="label">
                          {' '}
                          ( Per {data?.base_price?.unit_of_weight} )
                        </Typography>
                      )}
                    </span>
                  }
                </div>
                <div className={css.value}>
                  {data?.base_price != null
                    ? getCommaSeperatedCurrencyAmount(
                        data?.base_price?.currency,
                        Number(data?.base_price?.value)
                      )
                    : '-'}
                </div>
              </div>
              <div className={css.keyValue}>
                <div className={css.key}>
                  {
                    <span>
                      Ex Works Price
                      {data?.ex_works_price && (
                        <Typography variant="label">
                          {' '}
                          ( Per {data?.ex_works_price?.unit_of_weight} )
                        </Typography>
                      )}
                    </span>
                  }
                </div>
                <div className={css.value}>
                  {data?.ex_works_price != null
                    ? getCommaSeperatedCurrencyAmount(
                        data?.ex_works_price?.currency,
                        Number(data?.ex_works_price?.value)
                      )
                    : '-'}
                </div>
              </div>
            </div>
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>Total Local Transportation & Freight</div>
                <div className={css.value}>
                  {data?.total_local_freight != null
                    ? getCommaSeperatedCurrencyAmount(
                        data?.total_local_freight?.currency,
                        Number(data?.total_local_freight?.value)
                      )
                    : '-'}
                </div>
              </div>
              <div className={css.keyValue}>
                <div className={css.key}>Total port and clearance expenses</div>
                <div className={css.value}>
                  {data?.total_port_and_clearance_expenses != null
                    ? getCommaSeperatedCurrencyAmount(
                        data?.total_port_and_clearance_expenses?.currency,
                        Number(data?.total_port_and_clearance_expenses?.value)
                      )
                    : '-'}
                </div>
              </div>
            </div>
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>Total International freight per FCL </div>
                <div className={css.value}>
                  {data?.total_freight != null
                    ? getCommaSeperatedCurrencyAmount(
                        data?.total_freight?.currency,
                        Number(data?.total_freight?.value)
                      )
                    : '-'}
                </div>
              </div>
              <div className={css.keyValue}>
                <div className={css.key}>Total weight in one container</div>
                <div className={css.value}>
                  {Number(data?.weight_one_container)} {data?.weight_one_container_unit}
                </div>
              </div>
            </div>
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>Credit and Cargo insurance cost</div>
                <div className={css.value}>
                  {data?.insurance_cost ? ` ${sanitizeValue(data?.insurance_cost)} % ` : '-'}
                </div>
              </div>
              <div className={css.keyValue}>
                <div className={css.key}>Switch BL Cost</div>
                <div className={css.value}>
                  {data?.switch_bl_cost != null
                    ? getCommaSeperatedCurrencyAmount(
                        data?.switch_bl_cost?.currency,
                        Number(data?.switch_bl_cost?.value)
                      )
                    : '-'}
                </div>
              </div>
            </div>
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>
                  {
                    <span>
                      FOB Price
                      {data?.fob_price && (
                        <Typography variant="label">
                          {' '}
                          ( Per {data?.fob_price?.unit_of_weight} )
                        </Typography>
                      )}
                    </span>
                  }
                </div>
                <div className={css.value}>
                  {data?.fob_price != null
                    ? getCommaSeperatedCurrencyAmount(
                        data?.fob_price?.currency,
                        Number(data?.fob_price?.value)
                      )
                    : '-'}
                </div>
              </div>
              <div className={css.keyValue}>
                <div className={css.key}>
                  {
                    <span>
                      CFR Price
                      {data?.cfr_price && (
                        <Typography variant="label">
                          {' '}
                          ( Per {data?.cfr_price?.unit_of_weight} )
                        </Typography>
                      )}
                    </span>
                  }
                </div>
                <div className={css.value}>
                  {data?.cfr_price != null
                    ? getCommaSeperatedCurrencyAmount(
                        data?.cfr_price?.currency,
                        Number(data?.cfr_price?.value)
                      )
                    : '-'}
                </div>
              </div>
            </div>
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>
                  {
                    <span>
                      CIF Price
                      {data?.cif_price && (
                        <Typography variant="label">
                          {' '}
                          ( Per {data?.cif_price?.unit_of_weight} )
                        </Typography>
                      )}
                    </span>
                  }
                </div>
                <div className={css.value}>
                  {data?.cif_price != null
                    ? getCommaSeperatedCurrencyAmount(
                        data?.cif_price?.currency,
                        Number(data?.cif_price?.value)
                      )
                    : '-'}
                </div>
              </div>
              <div className={css.keyValue}>
                <div className={css.key}>
                  {
                    <span>
                      Final Price
                      {data?.final_price && (
                        <Typography variant="label">
                          {' '}
                          ( Per {data?.final_price?.unit_of_weight} )
                        </Typography>
                      )}
                    </span>
                  }
                </div>
                <div className={css.value}>
                  {data?.final_price != null
                    ? getCommaSeperatedCurrencyAmount(
                        data?.final_price?.currency,
                        Number(data?.final_price?.value)
                      )
                    : '-'}
                </div>
              </div>
            </div>
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>POL</div>
                <div className={css.value}>{sanitizeValue(data.pol?.name)}</div>
              </div>
              <div className={css.keyValue}>
                <div className={css.key}>POD</div>
                <div className={css.value}>{sanitizeValue(data.pod?.name)}</div>
              </div>
            </div>
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>
                  {
                    <span>
                      Additional costs
                      {primaryUnit && (
                        <Typography variant="label"> ( Per {primaryUnit} )</Typography>
                      )}
                    </span>
                  }
                </div>
                <div className={css.value}>
                  {data?.additional_cost != null
                    ? getCommaSeperatedCurrencyAmount(
                        data?.additional_cost?.currency,
                        Number(data?.additional_cost?.value)
                      )
                    : '-'}
                </div>
              </div>
              <div className={css.keyValue}>
                <div className={css.key}>Finance cost</div>
                <div className={css.value}>
                  {data?.finance_cost ? ` ${sanitizeValue(data?.finance_cost)} % ` : '-'}
                </div>
              </div>
            </div>
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>Expected Profit Margin</div>
                <div className={css.value}>
                  {data?.profit_margin ? ` ${sanitizeValue(data?.profit_margin)} % ` : '0 %'}
                </div>
              </div>
            </div>
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>Expected CM1</div>
                <div className={css.value}>{getFormattedCM('cm1', data)}</div>
              </div>
              <div className={css.keyValue}>
                <div className={css.key}>Expected CM2</div>
                <div className={css.value}>{getFormattedCM('cm2', data)}</div>
              </div>
            </div>
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>Discount</div>
                <div className={css.value}>
                  {data?.discount ? ` ${sanitizeValue(data?.discount)} % ` : '-'}
                </div>
              </div>
              <div className={css.keyValue}>
                <div className={css.key}>Tariff</div>
                <div className={css.value}>
                  {data?.tariff ? ` ${sanitizeValue(data?.tariff)} % ` : '-'}
                </div>
              </div>
            </div>
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>Incoterms </div>
                <div className={css.value}>{sanitizeValue(data?.deal_incoterm)}</div>
              </div>
              <div className={css.keyValue}>
                <div className={css.key}>
                  {data?.deal_incoterm === 'EXW' ? 'Ex Works Location' : 'Place of Discharge'}
                </div>
                <div className={css.value}>{sanitizeValue(data?.deal_incoterm_location)}</div>
              </div>
            </div>
            {data?.deal_incoterm === 'DDP' && (
              <>
                <div className={css.rowWrapper}>
                  <div className={css.keyValue}>
                    <div className={css.key}>
                      {
                        <span>
                          DDP Price
                          {data?.ddp_price && (
                            <Typography variant="label">
                              {' '}
                              ( Per {data?.ddp_price?.unit_of_weight} )
                            </Typography>
                          )}
                        </span>
                      }
                    </div>
                    <div className={css.value}>
                      {data?.ddp_price != null
                        ? getCommaSeperatedCurrencyAmount(
                            data?.ddp_price?.currency,
                            Number(data?.ddp_price?.value)
                          )
                        : '-'}
                    </div>
                  </div>
                  <div className={css.keyValue}>
                    <div className={css.key}>
                      {
                        <span>
                          Drayage and Transportation Costs
                          {data?.transportation_cost && (
                            <Typography variant="label">
                              {' '}
                              ( Per {data?.transportation_cost?.currency} )
                            </Typography>
                          )}
                        </span>
                      }
                    </div>
                    <div className={css.value}>{data?.transportation_cost?.value}</div>
                  </div>
                </div>
                <div className={css.rowWrapper}>
                  <div className={css.keyValue}>
                    <div className={css.key}>
                      {
                        <span>
                          Custom / ISF Filing Charges
                          {data?.isf_filing && (
                            <Typography variant="label">
                              {' '}
                              ( Per {data?.isf_filing?.currency} )
                            </Typography>
                          )}
                        </span>
                      }
                    </div>
                    <div className={css.value}>{data?.isf_filing?.value}</div>
                  </div>
                  <div className={css.keyValue}>
                    <div className={css.key}>Custom Clearance cost</div>
                    <div className={css.value}>
                      {data?.finance_cost ? ` ${sanitizeValue(data?.custom_duty)} % ` : '-'}
                    </div>
                  </div>
                </div>
                <div className={css.rowWrapper}>
                  <div className={css.keyValue}>
                    <div className={css.key}>Container Size</div>
                    <div className={css.value}>{sanitizeValue(data?.container_size)}</div>
                  </div>
                </div>
              </>
            )}
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>Country of Origin</div>
                <div className={css.value}>
                  {sanitizeValue(data?.origin_display_value ?? data?.origin)}
                </div>
              </div>
              {data?.deal_incoterm === 'DDP' && (
                <div className={css.keyValue}>
                  <div className={css.key}>Zip Code</div>
                  <div className={css.value}>{sanitizeValue(data?.zip_code)}</div>
                </div>
              )}
            </div>
            <div className={css.rowWrapper}>
              <div className={css.keyValue}>
                <div className={css.key}>Material Location Country</div>
                <div className={css.value}>
                  {sanitizeValue(
                    data?.material_location_country_display_value ?? data?.material_location_country
                  )}
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </Modal>
  );
};

export default ViewQuoteModal;
